// Phase 2.1 polish E — invoice upload helper. Posts a File to the api-server
// endpoint and returns the public Supabase Storage URL.
//
// If the api-server is missing SUPABASE_URL / SUPABASE_SERVICE_ROLE_KEY env vars
// it returns 503 — we surface a clear "Upload not configured" error so the
// operator knows it's a setup issue, not their file.

const PROD_API_BASE = "https://de2uveuyxp2cc.cloudfront.net";

function apiBase(): string {
  return (
    (import.meta.env.VITE_API_BASE as string | undefined) ??
    (import.meta.env.PROD ? PROD_API_BASE : "")
  );
}

export type UploadResult =
  | { ok: true; url: string; path: string }
  | { ok: false; status: number; message: string };

// 2026-05-20: client-side image compression before upload. Phone photos of
// invoices/items are often 3-5 MB; downscaling to ~1600px max edge + JPEG q0.72
// brings them to a few hundred KB without losing legibility. PDFs and non-images
// pass through untouched. If anything fails, the original file is used as-is.
/**
 * Thrown when the input is HEIC/HEIF (iPhone default) and the browser
 * can't decode it. The upload dialog catches this and surfaces a clear
 * message asking the operator to reshare as JPEG/PNG (or flip the
 * iPhone camera Formats setting to "Most Compatible").
 */
export class HeicUnsupportedError extends Error {
  constructor(msg?: string) {
    super(msg ?? "iPhone HEIC photos aren't supported. On iPhone: Settings → Camera → Formats → Most Compatible, OR share the image as JPEG.");
    this.name = "HeicUnsupportedError";
  }
}

function isHeicLike(file: File): boolean {
  const t = (file.type || "").toLowerCase();
  const n = (file.name || "").toLowerCase();
  return t === "image/heic" || t === "image/heif" || t === "image/heic-sequence"
      || n.endsWith(".heic") || n.endsWith(".heif");
}

async function compressImage(
  file: File,
  maxDim = 1600,
  quality = 0.72,
  targetBytes = 600 * 1024, // aim for ≤ ~600 KB in the bucket
): Promise<File> {
  if (!file.type.startsWith("image/") || file.type === "image/gif") return file;
  const heic = isHeicLike(file);
  try {
    const bitmap = await createImageBitmap(file);
    const scale = Math.min(1, maxDim / Math.max(bitmap.width, bitmap.height));
    const w = Math.round(bitmap.width * scale);
    const h = Math.round(bitmap.height * scale);
    const canvas = document.createElement("canvas");
    canvas.width = w;
    canvas.height = h;
    const ctx = canvas.getContext("2d");
    if (!ctx) return file;
    ctx.drawImage(bitmap, 0, 0, w, h);
    const toBlob = (q: number) =>
      new Promise<Blob | null>((resolve) => canvas.toBlob(resolve, "image/jpeg", q));
    // First pass at the requested quality, then step quality down until the
    // encoded size is under the target (keeps high-detail photos in KB range).
    let q = quality;
    let blob = await toBlob(q);
    while (blob && blob.size > targetBytes && q > 0.4) {
      q = Math.round((q - 0.1) * 100) / 100;
      blob = await toBlob(q);
    }
    bitmap.close?.();
    if (!blob) {
      if (heic) throw new HeicUnsupportedError();
      return file;
    }
    // HEIC path: always return the JPEG (server rejects HEIC MIME even if
    // the compressed version happens to be larger than the source).
    if (heic || blob.size < file.size) {
      return new File([blob], file.name.replace(/\.[^.]+$/, ".jpg"), { type: "image/jpeg" });
    }
    return file;
  } catch (err) {
    if (heic) throw new HeicUnsupportedError();
    if (err instanceof HeicUnsupportedError) throw err;
    return file;
  }
}

async function uploadTo(
  endpoint: string,
  rawFile: File,
  opts?: { compress?: boolean },
): Promise<UploadResult> {
  // Logos skip compression — compressImage re-encodes to JPEG, which flattens
  // transparency (a transparent PNG logo would gain a white box).
  const file = opts?.compress === false ? rawFile : await compressImage(rawFile);
  const fd = new FormData();
  fd.append("file", file);
  const token = localStorage.getItem("crm_token");
  const res = await fetch(`${apiBase()}${endpoint}`, {
    method: "POST",
    headers: token ? { Authorization: `Bearer ${token}` } : undefined,
    body: fd,
  });
  if (res.ok) {
    const json = (await res.json()) as { url: string; path: string };
    return { ok: true, url: json.url, path: json.path };
  }
  let message = `HTTP ${res.status}`;
  try {
    const j = (await res.json()) as { error?: string; hint?: string; message?: string };
    if (res.status === 503) {
      message = j.hint ?? j.error ?? "Upload not configured";
    } else {
      message = j.error ?? j.message ?? message;
    }
  } catch {
    /* response body not JSON */
  }
  return { ok: false, status: res.status, message };
}

export async function uploadInvoice(file: File): Promise<UploadResult> {
  return uploadTo("/api/uploads/invoice", file);
}

// 2026-05-19 hot-fix: per-item photos (PNG / JPEG / WebP only, 5MB cap server-side).
export async function uploadItemPhoto(file: File): Promise<UploadResult> {
  return uploadTo("/api/uploads/item-photo", file);
}

// Brand logo for invoice/receipt templates. No compression → transparency kept.
export async function uploadLogo(file: File): Promise<UploadResult> {
  return uploadTo("/api/uploads/logo", file, { compress: false });
}

// 2026-05-21: stock-in payment transaction proof — own bucket (stock-in-payment-proofs).
export async function uploadPaymentProof(file: File): Promise<UploadResult> {
  return uploadTo("/api/uploads/payment-proof", file);
}

// Phase 42 Plan 09 (TPRF-02/03) — checklist item proof (photo evidence OR a
// captured signature image, both PNG/JPEG/WebP). Mirrors uploadItemPhoto
// exactly, incl. client-side compressImage — housekeeping shoot these on
// low-end Androids over patchy wifi (TCHK-04's constraint), so an
// uncompressed 8MP upload is a failed tick.
export async function uploadChecklistProof(file: File): Promise<UploadResult> {
  return uploadTo("/api/uploads/checklist-proof", file);
}

// Phase 23: Account document upload (KYC, agreement, deposit_receipt, handover, exit_settlement).
// Images are compressed client-side using compressImage before posting; PDFs pass through verbatim.
// Returns the storage path key, DB document id, and a short-lived signed URL for immediate display.
export async function uploadDocument(
  accountId: number,
  kind:
    | "agreement"
    | "kyc"
    | "deposit_receipt"
    | "handover"
    | "exit_settlement"
    | "company_registration"
    | "gst_certificate"
    | "gst_return",
  file: File,
  options?: { contactId?: number; signedAt?: string },
): Promise<{ url: string; documentId: number; signedUrl?: string }> {
  // Compress images client-side (matches uploadTo compressImage path; ~600 KB target).
  let payload: File = file;
  if (file.type.startsWith("image/")) {
    payload = await compressImage(file);
  }

  const formData = new FormData();
  formData.append("file", payload);
  formData.append("type", kind);
  if (options?.contactId !== undefined) {
    formData.append("contactId", String(options.contactId));
  }
  if (options?.signedAt) {
    formData.append("signedAt", options.signedAt);
  }

  const token = localStorage.getItem("crm_token");
  // No `credentials: "include"` — auth is the Bearer token header, not cookies.
  // The api-server uses wildcard CORS (`app.use(cors())` → ACAO: *), and the
  // browser forbids a wildcard ACAO on credentialed requests, which 500s the
  // preflight ("Failed to fetch"). This matches the other upload helpers.
  const response = await fetch(`${apiBase()}/api/accounts/${accountId}/documents`, {
    method: "POST",
    headers: token ? { Authorization: `Bearer ${token}` } : undefined,
    body: formData,
  });

  if (!response.ok) {
    let message = `HTTP ${response.status}`;
    try {
      const j = (await response.json()) as { code?: string; error?: string; message?: string };
      if (response.status === 503) {
        message = j.error ?? "Upload not configured";
      } else {
        // Prefer the detailed error (e.g. the Supabase storage message about an
        // unsupported MIME type / size) over the generic code, so failures are
        // actionable instead of "storage_upload_failed".
        message = j.error ?? j.message ?? j.code ?? message;
      }
    } catch {
      /* response body not JSON */
    }
    throw new Error(message);
  }

  const data = (await response.json()) as { fileUrl: string; id: number; signedUrl?: string };
  return { url: data.fileUrl, documentId: data.id, signedUrl: data.signedUrl };
}
